/** Load the first four eligible outlet cards in Shopify's collection-wide order. */
export async function loadOutletCards({ url, sort, fetcher = fetch, parse = (html) => new DOMParser().parseFromString(html, 'text/html'), signal }) {
  let next = new URL(url, window.location.origin);
  const cards = [];
  const visited = new Set();
  while (next && cards.length < 4) {
    if (next.origin !== window.location.origin || !next.pathname.endsWith('/collections/outlet-1')) {
      throw new Error('Archive products must come from outlet-1.');
    }
    next.searchParams.set('view', 'solstice-outlet-cards');
    next.searchParams.set('sort_by', sort === 'created-descending' ? sort : 'best-selling');
    if (visited.has(next.href)) throw new Error('Repeated outlet collection page.');
    visited.add(next.href);
    const response = await fetcher(next.href, { signal, credentials: 'same-origin' });
    if (!response.ok) throw new Error(`Outlet products request failed (${response.status}).`);
    const page = parse(await response.text());
    cards.push(...page.querySelectorAll('solstice-product-card'));
    const href = page.querySelector('[data-outlet-next]')?.getAttribute('href');
    next = href ? new URL(href, next) : null;
  }
  return cards.slice(0, 4);
}

class OutletProducts extends HTMLElement {
  #controller;
  connectedCallback() {
    if (this.dataset.loaded === 'true' || this.#controller) return;
    const controller = new AbortController();
    this.#controller = controller;
    this.setAttribute('aria-busy', 'true');
    loadOutletCards({ url: this.dataset.collectionUrl, sort: this.dataset.sort, signal: controller.signal })
      .then((cards) => {
        if (controller.signal.aborted) return;
        const wrappers = cards.map((card) => {
          const wrapper = document.createElement('div');
          wrapper.className = this.dataset.cardClass;
          wrapper.append(card);
          return wrapper;
        });
        this.replaceChildren(...wrappers);
        this.dataset.loaded = 'true';
        if (!cards.length) this.closest('section').hidden = true;
      })
      .catch((error) => {
        if (controller.signal.aborted) return;
        console.warn('[solstice-outlet-products]', error);
        // Keep the collection link available without substituting unrelated products.
        this.replaceChildren();
      })
      .finally(() => {
        if (this.#controller === controller) {
          this.removeAttribute('aria-busy');
          this.#controller = null;
        }
      });
  }
  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = null;
  }
}
if (!customElements.get('solstice-outlet-products')) {
  customElements.define('solstice-outlet-products', OutletProducts);
}
